import { useFieldArray, useFormContext, useWatch } from '@astro/forms'
import { TextInput } from '../fields'
import SelectField from '../SelectField'
import { DAY_OPTIONS, DAILY_OPTION } from '../registerSteps'

/** Time slots inside one day row: first line carries "+", the rest "✕". */
function SlotRows({ dayIndex }) {
  const { control } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: `availability.${dayIndex}.slots`,
    rules: { required: 'Add at least one time slot.' },
  })
  return (
    <div>
      <span className="ar-label">Time Slots</span>
      <div className="space-y-3">
        {fields.map((slot, j) => (
          <div key={slot.id} className="flex items-start gap-3">
            <div className="flex-1">
              <TextInput
                name={`availability.${dayIndex}.slots.${j}.from`}
                type="time"
                rules={{ required: 'Start time is required.' }}
              />
            </div>
            <div className="flex-1">
              <TextInput
                name={`availability.${dayIndex}.slots.${j}.to`}
                type="time"
                rules={{
                  required: 'End time is required.',
                  validate: (value, formValues) => {
                    const from = formValues.availability?.[dayIndex]?.slots?.[j]?.from
                    return !from || value > from || 'End time must be after start time.'
                  },
                }}
              />
            </div>
            {j === 0 ? (
              <button
                type="button"
                onClick={() => append({ from: '', to: '' })}
                className="w-10 h-[43px] shrink-0 rounded-xl bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-lg leading-none shadow-md shadow-goldPrimary/20 transition-transform hover:-translate-y-0.5"
                aria-label="Add time slot"
              >
                +
              </button>
            ) : (
              <button
                type="button"
                onClick={() => remove(j)}
                className="w-10 h-[43px] shrink-0 rounded-xl border-[1.5px] border-red-300 text-red-500 font-bold text-sm leading-none transition-colors hover:bg-red-500 hover:border-red-500 hover:text-white"
                aria-label="Remove time slot"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function StepAvailability() {
  const { control, getValues, formState: { errors } } = useFormContext()
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'availability',
    rules: { required: 'Add at least one availability day.' },
  })

  const rows = useWatch({ name: 'availability' }) ?? []
  const isDaily = rows.some((r) => r?.day === DAILY_OPTION)
  const usedDays = rows.map((r) => r?.day).filter(Boolean)

  // Picking "Daily" makes the other day rows redundant — collapse to one.
  const handleDayChange = (i) => (value) => {
    if (value === DAILY_OPTION && fields.length > 1) {
      replace([{ day: DAILY_OPTION, slots: getValues(`availability.${i}.slots`) }])
    }
  }

  return (
    <div className="step-panel space-y-4">
      <div className="p-3 rounded-xl bg-amber-50/60 border border-amber-800/15 flex items-start gap-2.5">
        <span className="text-sm">🗓️</span>
        <p className="text-xs text-stone-500 font-light leading-relaxed">
          Set your weekly consultation windows — several time slots per day are fine. Pick{' '}
          <strong className="font-semibold">Daily (Mon–Sun)</strong> to apply the same slots to every day of the week.
        </p>
      </div>

      <div className="space-y-4">
        {fields.map((row, i) => (
          <div key={row.id} className="ar-repeat-row">
            {fields.length > 1 && (
              <button type="button" className="ar-remove-row" onClick={() => remove(i)} aria-label="Remove day">
                ✕
              </button>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4 pr-6">
              <SelectField
                name={`availability.${i}.day`}
                label="Day"
                options={DAY_OPTIONS.filter(
                  (d) => d.value === rows[i]?.day || !usedDays.includes(d.value),
                )}
                placeholder="Select day"
                rules={{ required: 'Select a day.' }}
                onChangeValue={handleDayChange(i)}
              />
              <SlotRows dayIndex={i} />
            </div>
          </div>
        ))}
      </div>

      {errors.availability?.root && <p className="ar-error">{errors.availability.root.message}</p>}

      {!isDaily && (
        <button
          type="button"
          onClick={() => append({ day: '', slots: [{ from: '', to: '' }] })}
          className="w-full border border-dashed border-goldAccent/40 text-goldAccent hover:bg-goldPrimary/5 font-bold text-xs tracking-wider uppercase py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <span className="text-base leading-none">+</span> Add Day
        </button>
      )}
    </div>
  )
}
